import { OptimizationResults, OptimizationResultsWithStats } from '../types';

export interface CombinedWinnerResult {
  perfectWinners: Array<{
    name: string;
    key: string;
    fitness: number;
    energy: number;
    fitnessRank: number;
    energyRank: number;
  }>;
  bestAlternative: {
    name: string;
    key: string;
    fitness: number;
    energy: number;
    fitnessRank: number;
    energyRank: number;
  } | null;
  rankedAlgorithms: Array<{
    name: string;
    key: string;
    fitness: number;
    energy: number;
    fitnessRank: number;
    energyRank: number;
    isPerfectWinner: boolean;
    isBestAlternative: boolean;
  }>;
  hasPerfectWinner: boolean;
  bestFitness: number;
  bestEnergy: number;
}

/**
 * Calculates combined winner using:
 * - Primary: highest median fitness + lowest median energy (Pareto-consistent)
 * - Secondary: highest median composite score
 * - Stability: lowest standard deviation
 * 
 * If statistics are available (multiple runs), uses MEDIAN values across all runs
 * Otherwise, uses the single run's values
 */
export function calculateCombinedWinner(results: OptimizationResults | OptimizationResultsWithStats): CombinedWinnerResult | null {
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
  const algorithmNames = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];

  // Check if statistics are available (multiple runs were performed)
  const hasStatistics = 'statistics' in results && results.statistics !== undefined;
  const hasCompositeScores = 'compositeScores' in results && results.compositeScores !== undefined;

  // Get all algorithms with results
  // If statistics available, use MEDIAN values across all runs; otherwise use single run values
  const allAlgorithms = algorithmKeys
    .map((key, index) => {
      const result = results[key];
      if (!result) return null;
      
      let fitness: number;
      let energy: number;
      let fitnessStdDev: number = 0;
      let energyStdDev: number = 0;
      let compositeScore: number | null = null;
      let compositeScoreStdDev: number = 0;
      
      if (hasStatistics && results.statistics) {
        // Use MEDIAN values from statistics (across all runs) - more robust than mean
        fitness = results.statistics[key].fitness.median;
        energy = results.statistics[key].energy.median;
        fitnessStdDev = results.statistics[key].fitness.stdDev;
        energyStdDev = results.statistics[key].energy.stdDev;
      } else {
        // Use single run values
        fitness = result.fitness;
        energy = result.energy;
      }
      
      // Get composite score if available
      if (hasCompositeScores && results.compositeScores && results.compositeScores[key]) {
        compositeScore = results.compositeScores[key].median;
        compositeScoreStdDev = results.compositeScores[key].stdDev;
      }
      
      return {
        name: algorithmNames[index],
        key: algorithmKeys[index],
        fitness,
        energy,
        fitnessStdDev,
        energyStdDev,
        compositeScore,
        compositeScoreStdDev,
      };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

  if (allAlgorithms.length === 0) return null;

  // Helper function to compare floating point numbers with epsilon tolerance
  const isEqual = (a: number, b: number, epsilon = 1e-9): boolean => {
    return Math.abs(a - b) < epsilon;
  };

  // Sort by fitness (descending - higher is better) and energy (ascending - lower is better)
  const fitnessSorted = [...allAlgorithms].sort((a, b) => {
    // Primary: fitness (descending)
    if (Math.abs(a.fitness - b.fitness) > 1e-9) {
      return b.fitness - a.fitness;
    }
    // Tie-breaker: lower stdDev (more stable)
    return a.fitnessStdDev - b.fitnessStdDev;
  });
  
  const energySorted = [...allAlgorithms].sort((a, b) => {
    // Primary: energy (ascending - lower is better)
    if (Math.abs(a.energy - b.energy) > 1e-9) {
      return a.energy - b.energy;
    }
    // Tie-breaker: lower stdDev (more stable)
    return a.energyStdDev - b.energyStdDev;
  });

  // Find algorithms that are #1 in BOTH fitness AND energy (using median)
  const bestFitness = fitnessSorted[0].fitness;
  const bestEnergy = energySorted[0].energy;
  
  // If composite scores are available, also consider them for secondary ranking
  const compositeSorted = allAlgorithms
    .filter(a => a.compositeScore !== null)
    .sort((a, b) => {
      if (a.compositeScore === null || b.compositeScore === null) return 0;
      // Primary: composite score (descending)
      if (Math.abs(a.compositeScore - b.compositeScore) > 1e-9) {
        return b.compositeScore - a.compositeScore;
      }
      // Tie-breaker: lower stdDev (more stable)
      return a.compositeScoreStdDev - b.compositeScoreStdDev;
    });

  // Find the algorithm(s) that are #1 in fitness (using floating point comparison)
  const fitnessWinners = allAlgorithms.filter((alg) => isEqual(alg.fitness, bestFitness));
  
  // Find the algorithm(s) that are #1 in energy (using floating point comparison)
  const energyWinners = allAlgorithms.filter((alg) => isEqual(alg.energy, bestEnergy));

  // Check if there's an algorithm that wins BOTH fitness AND energy
  const perfectWinners = allAlgorithms.filter(
    (alg) => isEqual(alg.fitness, bestFitness) && isEqual(alg.energy, bestEnergy)
  );

  // If no perfect winner, find the best alternative using:
  // 1. Primary: highest median fitness + lowest median energy (Pareto-consistent)
  // 2. Secondary: highest median composite score
  // 3. Stability: lowest standard deviation
  let bestAlternative: typeof allAlgorithms[0] | null = null;
  
  if (perfectWinners.length === 0) {
    // Strategy: Find algorithm that best balances fitness and energy
    // Among fitness winners, find the one with lowest energy
    const bestFitnessLowestEnergy = fitnessWinners.reduce((best, current) => {
      if (current.energy < best.energy) return current;
      if (Math.abs(current.energy - best.energy) < 1e-9 && current.energyStdDev < best.energyStdDev) {
        return current; // Tie-break with stability
      }
      return best;
    });
    
    // Among energy winners, find the one with best fitness
    const lowestEnergyBestFitness = energyWinners.reduce((best, current) => {
      if (current.fitness > best.fitness) return current;
      if (Math.abs(current.fitness - best.fitness) < 1e-9 && current.fitnessStdDev < best.fitnessStdDev) {
        return current; // Tie-break with stability
      }
      return best;
    });
    
    // If composite scores are available, prefer algorithm with higher composite score
    if (compositeSorted.length > 0) {
      const bestComposite = compositeSorted[0];
      // Check if best composite is one of our candidates
      if (bestComposite.key === bestFitnessLowestEnergy.key || 
          bestComposite.key === lowestEnergyBestFitness.key) {
        bestAlternative = bestComposite;
      } else {
        // Choose based on normalized gaps, but prefer better composite score
        const fitnessWinnerGap = bestFitnessLowestEnergy.energy - bestEnergy;
        const energyWinnerGap = bestFitness - lowestEnergyBestFitness.fitness;
        
        const epsilon = 1e-9;
        if (Math.abs(bestEnergy) < epsilon || Math.abs(bestFitness) < epsilon) {
          bestAlternative = fitnessWinnerGap <= energyWinnerGap 
            ? bestFitnessLowestEnergy 
            : lowestEnergyBestFitness;
        } else {
          const normalizedFitnessGap = fitnessWinnerGap / bestEnergy;
          const normalizedEnergyGap = energyWinnerGap / bestFitness;
          
          bestAlternative = normalizedFitnessGap <= normalizedEnergyGap 
            ? bestFitnessLowestEnergy 
            : lowestEnergyBestFitness;
        }
      }
    } else {
      // No composite scores available, use gap-based selection
      const fitnessWinnerGap = bestFitnessLowestEnergy.energy - bestEnergy;
      const energyWinnerGap = bestFitness - lowestEnergyBestFitness.fitness;
      
      const epsilon = 1e-9;
      if (Math.abs(bestEnergy) < epsilon || Math.abs(bestFitness) < epsilon) {
        bestAlternative = fitnessWinnerGap <= energyWinnerGap 
          ? bestFitnessLowestEnergy 
          : lowestEnergyBestFitness;
      } else {
        const normalizedFitnessGap = fitnessWinnerGap / bestEnergy;
        const normalizedEnergyGap = energyWinnerGap / bestFitness;
        
        bestAlternative = normalizedFitnessGap <= normalizedEnergyGap 
          ? bestFitnessLowestEnergy 
          : lowestEnergyBestFitness;
      }
    }
  }

  // Calculate ranks for display
  const rankedAlgorithms = allAlgorithms.map((alg) => {
    const fitnessRank = fitnessSorted.findIndex((a) => a.key === alg.key) + 1;
    const energyRank = energySorted.findIndex((a) => a.key === alg.key) + 1;
    
    const isPerfectWinner = isEqual(alg.fitness, bestFitness) && isEqual(alg.energy, bestEnergy);
    const isBestAlternative = bestAlternative && alg.key === bestAlternative.key;
    
    return {
      ...alg,
      fitnessRank,
      energyRank,
      isPerfectWinner,
      isBestAlternative: !!isBestAlternative,
    };
  });

  // Sort: perfect winners first, then best alternative, then by fitness rank, then energy rank
  rankedAlgorithms.sort((a, b) => {
    if (a.isPerfectWinner && !b.isPerfectWinner) return -1;
    if (!a.isPerfectWinner && b.isPerfectWinner) return 1;
    if (a.isBestAlternative && !b.isBestAlternative) return -1;
    if (!a.isBestAlternative && b.isBestAlternative) return 1;
    if (a.fitnessRank !== b.fitnessRank) return a.fitnessRank - b.fitnessRank;
    return a.energyRank - b.energyRank;
  });

  return {
    perfectWinners: perfectWinners.map(winner => ({
      ...winner,
      fitnessRank: rankedAlgorithms.find(a => a.key === winner.key)?.fitnessRank || 1,
      energyRank: rankedAlgorithms.find(a => a.key === winner.key)?.energyRank || 1,
    })),
    bestAlternative: bestAlternative ? {
      ...bestAlternative,
      fitnessRank: rankedAlgorithms.find(a => a.key === bestAlternative!.key)?.fitnessRank || 1,
      energyRank: rankedAlgorithms.find(a => a.key === bestAlternative!.key)?.energyRank || 1,
    } : null,
    rankedAlgorithms,
    hasPerfectWinner: perfectWinners.length > 0,
    bestFitness,
    bestEnergy,
  };
}

